import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'

export const buttonVariantList = {
  color: {
    primary: '',
    'primary-neutral': '',
    destructive: '',
    neutral: '',
    success: '',
  },
  variant: {
    solid: '',
    outline: 'border',
    soft: '',
    ghost: 'bg-transparent',
    link: 'bg-transparent underline underline-offset-4',
    'link-inline': 'bg-transparent hover:underline underline-offset-4 !h-auto !px-0',
  },
  size: {
    '2xs': 'h-6 gap-1 px-2 text-xs',
    xs: 'h-7 gap-[5px] px-[10px] text-xs',
    sm: 'h-8 gap-1.5 px-3 text-xs',
    md: 'h-9 gap-[7px] px-[14px] text-sm',
    lg: 'h-11 gap-2 px-4 text-base',
    xl: 'h-12 gap-2 px-4 text-base',
    '2xl': 'h-14 gap-2.5 px-5 text-lg/[26px]',
  },
  selected: {
    false: '',
    true: '',
  },
  aspectSquare: {
    false: '',
    true: 'aspect-square px-0',
  },
}

export const buttonVariants = cva(
  [
    'inline-flex',
    'items-center',
    'justify-center',
    'whitespace-nowrap',
    'rounded-lg',
    'bg-clip-border',
    'font-medium',
    'outline-none',
    'ring-neutral-800',
    'ring-offset-2',
    'ring-offset-background',
    'transition-colors',
    'focus-visible:ring-2',
    'disabled:pointer-events-none',
    'disabled:opacity-50',
  ],
  {
    variants: buttonVariantList,
    compoundVariants: [
      // primary
      /// selected
      {
        color: 'primary',
        selected: true,
        class: 'border-accent-800 bg-accent-800 text-white',
      },
      {
        color: 'primary',
        variant: 'link',
        selected: true,
        class: 'bg-transparent text-accent-800',
      },
      {
        color: 'primary',
        variant: 'link-inline',
        selected: true,
        class: 'bg-transparent text-accent-800 underline',
      },
      /// variants
      {
        color: 'primary',
        variant: 'solid',
        selected: false,
        class: 'bg-accent-600 text-white hover:bg-accent-700',
      },
      {
        color: 'primary',
        variant: 'outline',
        selected: false,
        class: 'border-accent-600 text-accent-600 hover:bg-accent-50',
      },
      {
        color: 'primary',
        variant: 'soft',
        selected: false,
        class: 'bg-accent-50 text-accent-600 hover:bg-accent-100',
      },
      {
        color: 'primary',
        variant: 'ghost',
        selected: false,
        class: 'text-accent-600 hover:bg-accent-50',
      },
      {
        color: 'primary',
        variant: 'link',
        selected: false,
        class: 'text-accent-600 hover:text-accent-700',
      },
      {
        color: 'primary',
        variant: 'link-inline',
        selected: false,
        class: 'text-accent-600 hover:text-accent-700',
      },
      // primary-neutral
      /// selected
      {
        color: 'primary-neutral',
        selected: true,
        class: 'border-neutral-900 bg-neutral-900 text-white',
      },
      {
        color: 'primary-neutral',
        variant: 'link',
        selected: true,
        class: 'bg-transparent text-neutral-900',
      },
      {
        color: 'primary-neutral',
        variant: 'link-inline',
        selected: true,
        class: 'bg-transparent text-neutral-900 underline',
      },
      /// variants
      {
        color: 'primary-neutral',
        variant: 'solid',
        selected: false,
        class: 'bg-neutral-800 text-white hover:bg-neutral-900',
      },
      {
        color: 'primary-neutral',
        variant: 'outline',
        selected: false,
        class: 'border-neutral-800 text-neutral-800 hover:bg-neutral-100',
      },
      {
        color: 'primary-neutral',
        variant: 'soft',
        selected: false,
        class: 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200',
      },
      {
        color: 'primary-neutral',
        variant: 'ghost',
        selected: false,
        class: 'text-neutral-800 hover:bg-neutral-100',
      },
      {
        color: 'primary-neutral',
        variant: 'link',
        selected: false,
        class: 'text-neutral-800 hover:text-neutral-900',
      },
      {
        color: 'primary-neutral',
        variant: 'link-inline',
        selected: false,
        class: 'text-neutral-800 hover:text-neutral-900',
      },
      // destructive
      /// selected
      {
        color: 'destructive',
        selected: true,
        class: 'border-error-800 bg-error-800 text-white',
      },
      {
        color: 'destructive',
        variant: 'link',
        selected: true,
        class: 'bg-transparent text-error-800',
      },
      {
        color: 'destructive',
        variant: 'link-inline',
        selected: true,
        class: 'bg-transparent text-error-800 underline',
      },
      /// variants
      {
        color: 'destructive',
        variant: 'solid',
        selected: false,
        class: 'bg-error-400 text-white hover:bg-error-500',
      },
      {
        color: 'destructive',
        variant: 'outline',
        selected: false,
        class: 'border-error-600 text-error-600 hover:bg-error-50',
      },
      {
        color: 'destructive',
        variant: 'soft',
        selected: false,
        class: 'bg-error-50 text-error-600 hover:bg-error-100',
      },
      {
        color: 'destructive',
        variant: 'ghost',
        selected: false,
        class: 'text-error-600 hover:bg-error-50',
      },
      {
        color: 'destructive',
        variant: 'link',
        selected: false,
        class: 'text-error-600 hover:text-error-700',
      },
      {
        color: 'destructive',
        variant: 'link-inline',
        selected: false,
        class: 'text-error-600 hover:text-error-700',
      },
      // success
      /// selected
      {
        color: 'success',
        selected: true,
        class: 'border-success-800 bg-success-800 text-white',
      },
      {
        color: 'success',
        variant: 'link',
        selected: true,
        class: 'bg-transparent text-success-800',
      },
      {
        color: 'success',
        variant: 'link-inline',
        selected: true,
        class: 'bg-transparent text-success-800 underline',
      },
      /// variants
      {
        color: 'success',
        variant: 'solid',
        selected: false,
        class: 'bg-success-600 text-white hover:bg-success-700',
      },
      {
        color: 'success',
        variant: 'outline',
        selected: false,
        class: 'border-success-600 text-success-600 hover:bg-success-50',
      },
      {
        color: 'success',
        variant: 'soft',
        selected: false,
        class: 'bg-success-50 text-success-600 hover:bg-success-100',
      },
      {
        color: 'success',
        variant: 'ghost',
        selected: false,
        class: 'text-success-600 hover:bg-success-50',
      },
      {
        color: 'success',
        variant: 'link',
        selected: false,
        class: 'text-success-600 hover:text-success-700',
      },
      {
        color: 'success',
        variant: 'link-inline',
        selected: false,
        class: 'text-success-600 hover:text-success-700',
      },
      // neutral
      /// selected
      {
        color: 'neutral',
        selected: true,
        class: 'border-neutral-800 bg-neutral-800 text-white',
      },
      {
        color: 'neutral',
        variant: 'link',
        selected: true,
        class: 'bg-transparent text-neutral-800',
      },
      {
        color: 'neutral',
        variant: 'link-inline',
        selected: true,
        class: 'bg-transparent text-neutral-800 underline',
      },
      /// variants
      {
        color: 'neutral',
        variant: 'solid',
        selected: false,
        class: 'bg-neutral-600 text-white hover:bg-neutral-700',
      },
      {
        color: 'neutral',
        variant: 'outline',
        selected: false,
        class: 'border-border text-neutral-800 hover:bg-neutral-50',
      },
      {
        color: 'neutral',
        variant: 'soft',
        selected: false,
        class: 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200',
      },
      {
        color: 'neutral',
        variant: 'ghost',
        selected: false,
        class: 'text-neutral-800 hover:bg-neutral-800/[0.05] active:bg-neutral-800/[0.1]',
      },
      {
        color: 'neutral',
        variant: 'link',
        selected: false,
        class: 'text-neutral-800 hover:text-neutral-900',
      },
      {
        color: 'neutral',
        variant: 'link-inline',
        selected: false,
        class: 'text-neutral-800 hover:text-neutral-900',
      },
    ],
  },
)

export const buttonIconVariantList = {
  size: {
    '2xs': 'size-[12px]',
    xs: 'size-[14px]',
    sm: 'size-[16px]',
    md: 'size-[20px]',
    lg: 'size-[20px]',
    xl: 'size-[20px]',
    '2xl': 'size-[24px]',
  },
} satisfies Record<string, Record<NonNullable<ButtonVariants['size']>, string>>

export const buttonIconVariants = cva(
  '',
  {
    variants: buttonIconVariantList,
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
export type ButtonIconVariants = VariantProps<typeof buttonIconVariants>
